import { useCallback, useEffect, useMemo, useState, lazy, Suspense } from 'react';
import authService from '../../../../services/authService.js';

// Lazy load FaceIDManagement to avoid blocking the page
const FaceIDManagement = lazy(() => import('./FaceIDManagement'));

interface TwoFactorStatus {
  twoFactorEnabled: boolean;
  verifiedAt?: string;
  hasPendingSetup: boolean;
  availableBackupCodes: number;
  lastUpdatedAt?: string;
}

interface TwoFactorSetupData {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
  twoFactorEnabled: boolean;
}

const PAGINATION_SIZES: number[] = [5, 10, 20, 50];

const SecuritySettings = () => {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupData, setSetupData] = useState<TwoFactorSetupData | null>(null);
  const [otpToken, setOtpToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [disableOtp, setDisableOtp] = useState('');
  const [disableBackupCode, setDisableBackupCode] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [revokeLoadingId, setRevokeLoadingId] = useState<string | null>(null);
  const [revokeOthersLoading, setRevokeOthersLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const activeSessionCount = useMemo(
    () => sessions.filter((session) => !session.isRevoked && !session.revokedAt).length,
    [sessions]
  );
  const displayedSessions = sessions;

  const formatVnDateTime = (value?: string | null) => {
    if (!value) return 'Không rõ';
    let normalized = value.trim();
    if (normalized.endsWith('Z')) {
      normalized = normalized.slice(0, -1);
    }
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) {
      return 'Không rõ';
    }
    return date.toLocaleString('vi-VN', {
      timeZone: 'Asia/Ho_Chi_Minh',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await authService.twoFactor.getStatus();
      setStatus(data);
    } catch (err: any) {
      console.error('Fetch 2FA status error:', err);
      const message =
        err?.message ||
        err?.errorMessage ||
        err?.response?.data?.message ||
        'Không thể lấy trạng thái xác minh hai bước';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSessions = useCallback(async (paging?: { page?: number; pageSize?: number }) => {
    try {
      setSessionsLoading(true);
      setSessionsError(null);
      const requestPage = paging?.page ?? page;
      const requestPageSize = paging?.pageSize ?? pageSize;
      const data = await authService.sessions.list({ page: requestPage, pageSize: requestPageSize });
      const sessionList = data?.sessions || [];
      setSessions(sessionList);
      setCurrentSessionId(data?.currentSessionId || null);
      if (data?.pagination) {
        setPage(data.pagination.page);
        setPageSize(data.pagination.pageSize);
        setTotalPages(data.pagination.totalPages || 1);
        setTotalSessions(data.pagination.total || sessionList.length);
      } else {
        setPage(requestPage);
        setPageSize(requestPageSize);
        const total = sessionList.length;
        setTotalSessions(total);
        setTotalPages(Math.max(1, Math.ceil(total / requestPageSize)));
      }
    } catch (err: any) {
      console.error('Fetch sessions error:', err);
      const message =
        err?.message ||
        err?.errorMessage ||
        err?.response?.data?.message ||
        'Không thể tải danh sách phiên đăng nhập';
      setSessionsError(message);
    } finally {
      setSessionsLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchStatus();
    fetchSessions({ page, pageSize });
  }, [fetchStatus, fetchSessions]);

  const handleInitiateSetup = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      setSuccessMessage(null);
      setBackupCodes([]);
      const data = await authService.twoFactor.initiateSetup();
      setSetupData(data);
      setOtpToken('');
    } catch (err: any) {
      console.error('Initiate 2FA error:', err);
      const message =
        err?.message ||
        err?.errorMessage ||
        err?.response?.data?.message ||
        'Không thể khởi tạo 2FA';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmSetup = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setIsSubmitting(true);
      setError(null);
      const data = await authService.twoFactor.confirmSetup(otpToken);
      if (data?.backupCodes) {
        setBackupCodes(data.backupCodes);
      }
      setSuccessMessage('Đã bật xác minh hai bước. Vui lòng lưu lại các mã dự phòng bên dưới.');
      setSetupData(null);
      setOtpToken('');
      await fetchStatus();
      await fetchSessions({ page, pageSize });
    } catch (err: any) {
      console.error('Verify 2FA setup error:', err);
      const message =
        err?.message ||
        err?.errorMessage ||
        err?.response?.data?.message ||
        'Mã OTP không hợp lệ. Vui lòng thử lại.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisableTwoFactor = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setIsSubmitting(true);
      setError(null);
      await authService.twoFactor.disable({
        token: disableOtp || undefined,
        backupCode: disableBackupCode || undefined
      });
      setSuccessMessage('Đã tắt xác minh hai bước cho tài khoản.');
      setDisableOtp('');
      setDisableBackupCode('');
      setBackupCodes([]);
      setSetupData(null);
      await fetchStatus();
      await fetchSessions({ page, pageSize });
    } catch (err: any) {
      console.error('Disable 2FA error:', err);
      const message =
        err?.message ||
        err?.errorMessage ||
        err?.response?.data?.message ||
        'Không thể tắt xác minh hai bước. Vui lòng kiểm tra lại mã OTP hoặc mã dự phòng.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStatusBadge = () => {
    if (!status) return null;
    return status.twoFactorEnabled ? (
      <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold">
        <i className="ri-shield-check-line"></i>
        Đã bật
      </span>
    ) : (
      <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-sm font-semibold">
        <i className="ri-shield-cross-line"></i>
        Chưa bật
      </span>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="border-b border-gray-200 px-6 py-5">
        <h3 className="text-xl font-semibold text-gray-900">Bảo mật tài khoản</h3>
        <p className="mt-1 text-sm text-gray-600">
          Bảo vệ tài khoản quản trị bằng xác minh hai bước (2FA). Khuyến nghị bắt buộc cho tài khoản Admin.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {loading ? (
          <div className="flex items-center gap-3 text-gray-600">
            <div className="w-5 h-5 border-2 border-gray-200 border-t-[#2A4B7C] rounded-full animate-spin"></div>
            <span>Đang tải trạng thái bảo mật...</span>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                <div className="flex items-start gap-3">
                  <i className="ri-error-warning-line text-lg mt-0.5"></i>
                  <div>
                    <p className="font-semibold">Có lỗi xảy ra</p>
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                <div className="flex items-start gap-3">
                  <i className="ri-notification-badge-line text-lg mt-0.5"></i>
                  <div>
                    <p className="font-semibold">Thành công</p>
                    <p className="text-sm whitespace-pre-line">{successMessage}</p>
                  </div>
                </div>
              </div>
            )}

            {status && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-600 uppercase tracking-wide">Trạng thái hiện tại</p>
                  <div className="mt-2 flex items-center gap-3">
                    {renderStatusBadge()}
                    {status.hasPendingSetup && (
                      <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-semibold">
                        <i className="ri-time-line"></i>
                        Đang chờ xác minh
                      </span>
                    )}
                  </div>
                  <div className="mt-3 text-sm text-gray-600 space-y-1">
                    <p>
                      Mã dự phòng còn lại:{' '}
                      <span className="font-semibold text-gray-900">{status.availableBackupCodes}</span>
                    </p>
                    {status.verifiedAt && (
                      <p>Đã bật lần cuối: {new Date(status.verifiedAt).toLocaleString('vi-VN')}</p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleInitiateSetup}
                    disabled={isSubmitting}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#2A4B7C] text-white hover:bg-[#223760] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i className="ri-qr-code-line text-lg"></i>
                    {status.twoFactorEnabled ? 'Quét lại mã QR' : 'Bật xác minh hai bước'}
                  </button>
                  {status.twoFactorEnabled && (
                    <button
                      onClick={() => {
                        setDisableOtp('');
                        setDisableBackupCode('');
                        setSuccessMessage(null);
                        setError(null);
                      }}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <i className="ri-information-line text-lg"></i>
                      Nhập mã để tắt
                    </button>
                  )}
                </div>
              </div>
            )}

            {setupData && (
              <div className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <i className="ri-qr-scan-2-line text-xl text-[#2A4B7C]"></i>
                  Quét mã QR bằng ứng dụng xác thực
                </h4>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="flex flex-col items-center gap-3">
                    <img
                      src={setupData.qrCodeDataUrl}
                      alt="Two Factor QR"
                      className="w-48 h-48 border border-gray-200 rounded-lg shadow-inner"
                    />
                    <p className="text-sm text-gray-500 text-center">
                      Dùng Google Authenticator, Microsoft Authenticator hoặc ứng dụng hỗ trợ TOTP khác.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Không quét được QR? Nhập thủ công:</p>
                      <div className="mt-2 bg-gray-100 border border-gray-200 rounded-lg px-4 py-3 font-mono text-sm break-all">
                        {setupData.secret}
                      </div>
                    </div>
                    <form onSubmit={handleConfirmSetup} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mã OTP</label>
                        <input
                          type="text"
                          value={otpToken}
                          onChange={(event) => setOtpToken(event.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent text-sm tracking-widest text-center"
                          placeholder="Nhập mã 6 chữ số"
                          inputMode="numeric"
                          maxLength={6}
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#00C897] text-white hover:bg-[#00a67a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            Đang xác minh...
                          </>
                        ) : (
                          <>
                            <i className="ri-shield-check-line text-lg"></i>
                            Xác nhận và bật 2FA
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {backupCodes.length > 0 && (
              <div className="border border-green-200 rounded-lg p-6 bg-green-50">
                <h4 className="text-lg font-semibold text-green-800 flex items-center gap-2 mb-4">
                  <i className="ri-safe-2-line text-xl"></i>
                  Mã dự phòng (chỉ hiển thị một lần)
                </h4>
                <p className="text-sm text-green-700 mb-4">
                  Lưu cẩn thận các mã này. Mỗi mã chỉ sử dụng được một lần để đăng nhập khi không có điện thoại.
                </p>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {backupCodes.map((code) => (
                    <div
                      key={code}
                      className="bg-white text-green-900 border border-green-200 rounded-lg px-4 py-3 font-mono text-sm text-center shadow-sm"
                    >
                      {code}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {status?.twoFactorEnabled && (
              <div className="border border-red-200 rounded-lg p-6 bg-red-50">
                <h4 className="text-lg font-semibold text-red-800 flex items-center gap-2 mb-4">
                  <i className="ri-shield-user-line text-xl"></i>
                  Tắt xác minh hai bước
                </h4>
                <p className="text-sm text-red-700 mb-4">
                  Nhập mã OTP hiện tại hoặc một trong các mã dự phòng để tắt 2FA. Không khuyến khích trừ khi thật sự cần thiết.
                </p>
                <form onSubmit={handleDisableTwoFactor} className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mã OTP hiện tại
                    </label>
                    <input
                      type="text"
                      value={disableOtp}
                      onChange={(event) => setDisableOtp(event.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400 focus:border-transparent text-sm"
                      placeholder="Nhập mã OTP"
                      inputMode="numeric"
                      maxLength={6}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hoặc mã dự phòng
                    </label>
                    <input
                      type="text"
                      value={disableBackupCode}
                      onChange={(event) => setDisableBackupCode(event.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400 focus:border-transparent text-sm uppercase"
                      placeholder="Nhập mã dự phòng"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      disabled={isSubmitting || (!disableOtp && !disableBackupCode)}
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Đang xử lý...
                        </>
                      ) : (
                        <>
                          <i className="ri-shield-cross-line text-lg"></i>
                          Xác nhận tắt 2FA
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Face ID Management Section */}
            <Suspense fallback={
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="w-5 h-5 border-2 border-gray-200 border-t-[#2A4B7C] rounded-full animate-spin"></div>
                  <span>Đang tải quản lý Face ID...</span>
                </div>
              </div>
            }>
              <FaceIDManagement />
            </Suspense>

            <div className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <i className="ri-computer-line text-xl text-[#2A4B7C]"></i>
                    Phiên đăng nhập
                  </h4>
                  <p className="text-sm text-gray-600">
                    Theo dõi và quản lý các phiên đang đăng nhập bằng tài khoản admin của bạn.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchSessions({ page, pageSize })}
                    disabled={sessionsLoading}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-60"
                  >
                    <i className="ri-refresh-line"></i>
                    Làm mới
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm('Bạn chắc chắn muốn xoá (đăng xuất) tất cả các phiên đã đăng xuất khỏi danh sách?')) return;
                      try {
                        setRevokeOthersLoading(true);
                        await authService.sessions.clearRevoked();
                        setPage(1);
                        await fetchSessions({ page: 1, pageSize });
                        setSuccessMessage('Đã xoá các phiên đã đăng xuất khỏi danh sách.');
                      } catch (err: any) {
                        const message =
                          err?.message ||
                          err?.errorMessage ||
                          err?.response?.data?.message ||
                          'Không thể xoá phiên đã đăng xuất';
                        setError(message);
                      } finally {
                        setRevokeOthersLoading(false);
                      }
                    }}
                    disabled={revokeOthersLoading}
                    className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-100 transition disabled:opacity-60"
                  >
                    <i className="ri-delete-bin-6-line"></i>
                    Xoá phiên đã đăng xuất
                  </button>
                </div>
              </div>

              {sessionsError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-error-warning-line text-lg mt-0.5"></i>
                    <div>
                      <p className="font-semibold">Không thể tải phiên đăng nhập</p>
                      <p className="text-sm">{sessionsError}</p>
                    </div>
                  </div>
                </div>
              )}

              {sessionsLoading ? (
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="w-5 h-5 border-2 border-gray-200 border-t-[#2A4B7C] rounded-full animate-spin"></div>
                  <span>Đang tải danh sách phiên đăng nhập...</span>
                </div>
              ) : sessions.length === 0 ? (
                <div className="bg-gray-100 border border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-600">
                  Không có phiên đăng nhập nào được ghi nhận.
                </div>
              ) : (
                <div className="space-y-3">
                  {displayedSessions.map((session) => {
                    const isCurrent = session.sessionId === currentSessionId;
                    const isRevoked = session.isRevoked || !!session.revokedAt;
                    const ipDisplay = session.ipAddress || session.lastUsedIp || 'Không xác định';
                    const locationDisplay = session.location
                      ? [session.location.city, session.location.region, session.location.country].filter(Boolean).join(', ')
                      : null;
                    const loginTime = formatVnDateTime(session.createdAt);
                    const lastUsedTime = formatVnDateTime(session.lastUsedAt || session.updatedAt);
                    const revokedTime = formatVnDateTime(session.revokedAt);
                    return (
                      <div
                        key={session.sessionId}
                        className={`rounded-lg border p-4 transition ${isCurrent ? 'border-[#2A4B7C] bg-[#2A4B7C]/5' : 'border-gray-200 bg-gray-50'}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1 text-sm font-semibold text-gray-900">
                                <i className={`ri-${session.deviceType === 'mobile' ? 'smartphone-line' : session.deviceType === 'tablet' ? 'tablet-line' : 'computer-line'} text-lg`}></i>
                                {session.deviceInfo || 'Thiết bị không xác định'}
                              </span>
                              {isCurrent && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                                  <i className="ri-shield-check-line"></i>
                                  Phiên hiện tại
                                </span>
                              )}
                              {isRevoked && !isCurrent && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700">
                                  Đã đăng xuất
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">
                              IP: <span className="font-medium text-gray-800">{ipDisplay}</span>
                              {locationDisplay && (
                                <span className="ml-2 text-gray-500">({locationDisplay})</span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              Đăng nhập: {loginTime}
                              {' · '}
                              Hoạt động gần nhất: {lastUsedTime}
                              {isRevoked && session.revokedAt && (
                                <>
                                  {' · '}
                                  Thu hồi: {revokedTime}
                                </>
                              )}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-3">
                            {!isRevoked && (
                              <button
                                onClick={async () => {
                                  if (!window.confirm('Bạn chắc chắn muốn đăng xuất phiên này?')) return;
                                  try {
                                    setRevokeLoadingId(session.sessionId);
                                  await authService.sessions.revoke(session.sessionId);
                                    if (isCurrent) {
                                      authService.clearTokens();
                                      window.location.href = '/login';
                                    } else {
                                    await fetchSessions({ page, pageSize });
                                    setSuccessMessage('Đã đăng xuất phiên được chọn.');
                                    }
                                  } catch (err: any) {
                                    const message =
                                      err?.message ||
                                      err?.errorMessage ||
                                      err?.response?.data?.message ||
                                      'Không thể đăng xuất phiên được chọn';
                                    setError(message);
                                  } finally {
                                    setRevokeLoadingId(null);
                                  }
                                }}
                                disabled={revokeLoadingId === session.sessionId || isCurrent && revokeOthersLoading}
                                className="inline-flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 transition disabled:opacity-60"
                              >
                                {revokeLoadingId === session.sessionId ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                                    Đang đăng xuất...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-logout-box-line"></i>
                                    Đăng xuất phiên này
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {sessions.length > 0 && (
                <div className="mt-4 space-y-3 border-t border-gray-200 pt-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <p className="text-sm text-gray-600">
                        Tổng {totalSessions} phiên • Trang {page}/{totalPages}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">Sắp xếp</span>
                        <select
                          value="recent"
                          className="rounded-lg border border-gray-300 px-2 py-1 text-sm cursor-pointer"
                          disabled
                        >
                          <option value="recent">Mới nhất</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">Hiển thị</span>
                        <select
                          value={pageSize}
                          onChange={(event) => {
                            const newPageSize = parseInt(event.target.value, 10);
                            setPage(1);
                            setPageSize(newPageSize);
                            fetchSessions({ page: 1, pageSize: newPageSize });
                          }}
                          className="rounded-lg border border-gray-300 px-2 py-1 text-sm"
                        >
                          {PAGINATION_SIZES.map((size) => (
                            <option key={size} value={size}>
                              {size}/trang
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => {
                          const newPage = Math.max(page - 1, 1);
                          setPage(newPage);
                          fetchSessions({ page: newPage, pageSize });
                        }}
                        disabled={page <= 1}
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      >
                        <i className="ri-arrow-left-s-line"></i>
                        Trước
                      </button>
                      <span className="text-sm text-gray-600">
                        Trang {page}/{totalPages}
                      </span>
                      <button
                        onClick={() => {
                          const newPage = Math.min(page + 1, totalPages);
                          setPage(newPage);
                          fetchSessions({ page: newPage, pageSize });
                        }}
                        disabled={page >= totalPages}
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      >
                        Sau
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>

                    {activeSessionCount > 1 && (
                      <button
                        onClick={async () => {
                          if (!window.confirm('Bạn chắc chắn muốn đăng xuất tất cả các phiên khác?')) return;
                          try {
                            setRevokeOthersLoading(true);
                            await authService.sessions.revokeOthers();
                            setPage(1);
                            await fetchSessions({ page: 1, pageSize });
                            setSuccessMessage('Đã đăng xuất tất cả phiên khác.');
                          } catch (err: any) {
                            const message =
                              err?.message ||
                              err?.errorMessage ||
                              err?.response?.data?.message ||
                              'Không thể đăng xuất các phiên khác';
                            setError(message);
                          } finally {
                            setRevokeOthersLoading(false);
                          }
                        }}
                        disabled={revokeOthersLoading}
                        className="inline-flex items-center gap-2 rounded-lg bg-[#2A4B7C] px-4 py-2 text-sm font-semibold text-white hover:bg-[#223760] transition disabled:opacity-60"
                      >
                        {revokeOthersLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                            Đang xử lý...
                          </>
                        ) : (
                          <>
                            <i className="ri-shield-user-line"></i>
                            Đăng xuất các phiên khác
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            
          </>
        )}
      </div>
    </div>
  );
};

export default SecuritySettings;

